import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Camera, Image, Plus, Minus, Lock, Unlock } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCustomerDevices } from "@/hooks/use-customer-devices";
import { useDeviceAllocation } from "@/hooks/use-device-allocation";

interface DeviceAllocation {
  deviceId: string;
  deviceName?: string;
  allocatedCredits: number;
  isLocked?: boolean;
}

interface DeviceWithStatus {
  device_id: string;
  device_name: string;
  device_description: string;
  is_assigned: boolean;
  assigned_to_user?: string;
  assigned_at?: string;
}

interface DeviceAllocationSectionProps {
  orderId: string;
  totalCameras: number;
  totalImages: number;
  isExpanded: boolean;
  onToggle: () => void;
  onSave: (allocations: DeviceAllocation[]) => void;
}

export const DeviceAllocationSection = ({
  orderId,
  totalCameras,
  totalImages,
  isExpanded,
  onToggle,
  onSave
}: DeviceAllocationSectionProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [allocations, setAllocations] = useState<DeviceAllocation[]>([]);
  const [isAlreadyAllocated, setIsAlreadyAllocated] = useState(false);
  const { saveDeviceAllocation } = useDeviceAllocation();
  
  // Detect if this is a credits-only allocation (zero cameras)
  const isCreditsOnlyMode = totalCameras === 0;

  // Fetch order data to check for existing allocations and get customer_user_id
  const { data: orderData } = useQuery({
    queryKey: ['order-data', orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders')
        .select('order_data, allocated_device_ids, customer_user_id')
        .eq('id', orderId)
        .single();
      
      if (error) throw error;
      return data;
    }
  });

  // Get unallocated devices for selection (only when not in credits-only mode)
  const { data: unallocatedDevices = [] } = useQuery({
    queryKey: ['unallocated-devices'],
    queryFn: async () => {
      if (isCreditsOnlyMode) return []; // Don't fetch unallocated devices in credits-only mode
      
      // Get all device assignments to find which devices are already assigned
      const { data: assignments, error: assignmentsError } = await supabase
        .from('device_assignments')
        .select('device_id');
      
      if (assignmentsError) throw assignmentsError;
      
      const assignedDeviceIds = assignments?.map(a => a.device_id) || [];
      
      // Get all devices that are not assigned
      const { data: devices, error: devicesError } = await supabase
        .from('device_details')
        .select('device_id, device_name, device_description, device_status')
        .not('device_id', 'in', `(${assignedDeviceIds.map(id => `"${id}"`).join(',')})`)
        .order('device_name');
      
      if (devicesError) throw devicesError;
      
      return devices || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !isCreditsOnlyMode, // Only run query when not in credits-only mode
  });

  // Get customer-specific devices for reference
  const { data: customerDevices = [] } = useCustomerDevices(orderData?.customer_user_id);

  // Initialize allocations when component mounts or when order data changes
  useEffect(() => {
    // Safely parse order_data as it could be a JSON object or string
    const orderDataParsed = typeof orderData?.order_data === 'string' 
      ? JSON.parse(orderData.order_data)
      : orderData?.order_data;

    if (orderDataParsed && orderDataParsed.device_allocations) {
      // Pre-populate from existing allocations - transform snake_case to camelCase
      const existingAllocations = orderDataParsed.device_allocations.map((allocation: any) => ({
        deviceId: allocation.device_id || allocation.deviceId || '',
        deviceName: allocation.device_name || allocation.deviceName || '',
        allocatedCredits: allocation.allocated_credits || allocation.allocatedCredits || 0,
        isLocked: allocation.is_locked || allocation.isLocked || false
      }));
      setAllocations(existingAllocations);
      setIsAlreadyAllocated(true);
    } else if (totalCameras > 0 && !isAlreadyAllocated) {
      // Create new allocations for camera orders
      const creditsPerDevice = Math.floor(totalImages / totalCameras);
      const remainder = totalImages % totalCameras;
      
      const newAllocations: DeviceAllocation[] = Array.from({ length: totalCameras }, (_, index) => ({
        deviceId: '',
        deviceName: '',
        allocatedCredits: creditsPerDevice + (index < remainder ? 1 : 0),
        isLocked: false
      }));
      
      setAllocations(newAllocations);
    } else if (isCreditsOnlyMode && !isAlreadyAllocated) {
      // Create single allocation slot for credits-only orders
      const newAllocations: DeviceAllocation[] = [{
        deviceId: '',
        deviceName: '',
        allocatedCredits: totalImages,
        isLocked: false
      }];
      
      setAllocations(newAllocations);
    }
  }, [totalCameras, totalImages, orderData, isAlreadyAllocated]);

  const handleDeviceChange = (index: number, deviceId: string) => {
    if (isAlreadyAllocated) return; // Prevent changes if already allocated
    
    // Get device information from appropriate source
    const device = isCreditsOnlyMode 
      ? customerDevices?.find(d => d.device_id === deviceId)
      : unallocatedDevices?.find(d => d.device_id === deviceId);
    
    const newAllocations = [...allocations];
    newAllocations[index] = {
      ...newAllocations[index],
      deviceId,
      deviceName: device?.device_name || device?.device_description || deviceId
    };
    setAllocations(newAllocations);
  };

  const handleCreditsChange = (index: number, newCredits: number) => {
    if (isAlreadyAllocated) return; // Prevent changes if already allocated
    
    // Validate the new value doesn't go negative
    if (newCredits < 0) return;
    
    const oldCredits = allocations[index].allocatedCredits;
    const creditDifference = newCredits - oldCredits;
    
    const newAllocations = [...allocations];
    newAllocations[index] = {
      ...newAllocations[index],
      allocatedCredits: newCredits
    };

    // Auto-balance if we're exceeding total and there are unlocked devices to adjust
    const currentTotal = newAllocations.reduce((sum, a) => sum + a.allocatedCredits, 0);
    
    if (currentTotal > totalImages && creditDifference > 0) {
      const excess = currentTotal - totalImages;
      const unlockedDevices = newAllocations
        .map((allocation, idx) => ({ allocation, index: idx }))
        .filter(({ allocation, index: idx }) => !allocation.isLocked && idx !== index && allocation.allocatedCredits > 0);
      
      if (unlockedDevices.length > 0) {
        // Distribute the excess reduction proportionally among unlocked devices
        let remainingExcess = excess;
        
        for (const { allocation, index: deviceIndex } of unlockedDevices) {
          const proportion = allocation.allocatedCredits / unlockedDevices.reduce((sum, { allocation: a }) => sum + a.allocatedCredits, 0);
          const reduction = Math.min(
            Math.floor(remainingExcess * proportion),
            allocation.allocatedCredits
          );
          
          newAllocations[deviceIndex].allocatedCredits -= reduction;
          remainingExcess -= reduction;
          
          if (remainingExcess <= 0) break;
        }
        
        // Handle any remaining excess by reducing from devices with credits
        while (remainingExcess > 0) {
          const deviceToReduce = unlockedDevices.find(({ allocation }) => allocation.allocatedCredits > 0);
          if (!deviceToReduce) break;
          
          const reductionIndex = newAllocations.findIndex((_, idx) => idx === deviceToReduce.index);
          newAllocations[reductionIndex].allocatedCredits -= 1;
          remainingExcess -= 1;
        }
      } else {
        // If no unlocked devices available, cap the current device at what's possible
        const maxPossibleCredits = totalImages - newAllocations
          .filter((_, idx) => idx !== index)
          .reduce((sum, a) => sum + a.allocatedCredits, 0);
        newAllocations[index].allocatedCredits = Math.max(0, maxPossibleCredits);
      }
    }
    
    setAllocations(newAllocations);
  };

  const toggleLock = (index: number) => {
    if (isAlreadyAllocated) return; // Prevent changes if already allocated
    
    const newAllocations = [...allocations];
    newAllocations[index] = {
      ...newAllocations[index],
      isLocked: !newAllocations[index].isLocked
    };
    setAllocations(newAllocations);
  };

  const addDeviceSlot = () => {
    if (isAlreadyAllocated) return; // Prevent changes if already allocated
    
    setAllocations([...allocations, { deviceId: '', deviceName: '', allocatedCredits: 0, isLocked: false }]);
  };

  const removeDeviceSlot = (index: number) => {
    if (isAlreadyAllocated || allocations.length <= 1) return; // Prevent changes if already allocated
    
    const removedCredits = allocations[index].allocatedCredits;
    const newAllocations = allocations.filter((_, i) => i !== index);
    
    // Redistribute the removed credits equally among remaining devices
    if (removedCredits > 0 && newAllocations.length > 0) {
      const creditsPerDevice = Math.floor(removedCredits / newAllocations.length);
      const remainder = removedCredits % newAllocations.length;
      
      newAllocations.forEach((allocation, idx) => {
        allocation.allocatedCredits += creditsPerDevice + (idx < remainder ? 1 : 0);
      });
    }
    
    setAllocations(newAllocations);
  };

  const redistributeCredits = () => {
    if (isAlreadyAllocated || allocations.length === 0) return; // Prevent changes if already allocated
    
    const creditsPerDevice = Math.floor(totalImages / allocations.length);
    const remainder = totalImages % allocations.length;
    
    const newAllocations = allocations.map((allocation, index) => ({
      ...allocation,
      allocatedCredits: creditsPerDevice + (index < remainder ? 1 : 0),
      isLocked: false
    }));
    
    setAllocations(newAllocations);
  };

  const handleSave = async () => {
    if (isAlreadyAllocated) return; // Prevent saving if already allocated
    
    // Validate all devices are selected
    const hasEmptyDevices = allocations.some(a => !a.deviceId);
    if (hasEmptyDevices) {
      toast({
        title: "Validation Error",
        description: "Please select a device for all allocation slots",
        variant: "destructive"
      });
      return;
    }

    // Validate total credits match
    const totalAllocated = allocations.reduce((sum, a) => sum + a.allocatedCredits, 0);
    if (totalAllocated !== totalImages) {
      toast({
        title: "Validation Error",
        description: `Total allocated credits (${totalAllocated}) must equal total images (${totalImages})`,
        variant: "destructive"
      });
      return;
    }

    try {
      // Create device allocations array for order_data
      const deviceAllocations = allocations.map(allocation => ({
        device_id: allocation.deviceId,
        device_name: allocation.deviceName,
        allocated_credits: allocation.allocatedCredits
      }));

      // Safely parse existing order_data
      const existingOrderData = typeof orderData?.order_data === 'string' 
        ? JSON.parse(orderData.order_data)
        : orderData?.order_data || {};

      // Update the order with device allocations AND allocated_device_ids
      const allocatedDeviceIds = allocations.map(allocation => allocation.deviceId);
      
      const { error: updateError } = await supabase
        .from('orders')
        .update({
          order_data: {
            ...existingOrderData,
            device_allocations: deviceAllocations
          },
          allocated_device_ids: allocatedDeviceIds,
          devices_allocated_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (updateError) {
        throw updateError;
      }

      // Use the authenticated hook to save device allocation
      await saveDeviceAllocation.mutateAsync({
        orderId,
        allocations: allocations.map(allocation => ({
          deviceId: allocation.deviceId,
          deviceName: allocation.deviceName,
          allocatedCredits: allocation.allocatedCredits
        }))
      });

      // Call the parent onSave function
      await onSave(allocations);
      
      setIsAlreadyAllocated(true); // Mark as allocated after successful save
    } catch (error) {
      console.error('Error saving device allocations:', error);
      // Error handling is done by the hook
    }
  };

  const totalAllocated = allocations.reduce((sum, a) => sum + a.allocatedCredits, 0);
  const remainingCredits = totalImages - totalAllocated;
  const isValid = totalAllocated === totalImages && allocations.every(a => a.deviceId);

  return (
    <Card className="border-blue-200 bg-blue-50">
      <Collapsible open={isExpanded} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-blue-100 transition-colors">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Camera className="h-5 w-5 text-blue-600" />
                Device Allocation
                <Badge variant="outline" className="ml-2">
                  {totalCameras} cameras, {totalImages} credits
                </Badge>
                {isAlreadyAllocated && (
                  <Badge variant="default" className="ml-2 bg-green-600">
                    Already Allocated
                  </Badge>
                )}
              </div>
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {((isCreditsOnlyMode && customerDevices.length === 0) || (!isCreditsOnlyMode && unallocatedDevices.length === 0)) && !isAlreadyAllocated ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  {isCreditsOnlyMode 
                    ? "No devices assigned to this customer. The customer needs to have devices assigned before credits can be allocated."
                    : "No unallocated devices available. All devices are currently assigned to users."
                  }
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm text-muted-foreground">
                      {isAlreadyAllocated 
                        ? `${isCreditsOnlyMode ? 'Credits have' : 'Devices have'} been allocated for this order` 
                        : isCreditsOnlyMode
                          ? `Allocate ${totalImages} image credits to customer's existing devices`
                          : `Allocate ${totalImages} image credits across ${totalCameras} purchased cameras (Select from unallocated devices)`
                      }
                    </p>
                    {!isNaN(remainingCredits) && !isNaN(totalAllocated) && !isNaN(totalImages) && (
                      <div className="flex items-center gap-4 text-sm">
                        <span className={`font-medium ${remainingCredits === 0 ? 'text-green-600' : 'text-orange-600'}`}>
                          Remaining: {remainingCredits.toLocaleString()} credits
                        </span>
                        <span className="text-muted-foreground">
                          Total: {totalAllocated.toLocaleString()} / {totalImages.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                  {!isAlreadyAllocated && !isCreditsOnlyMode && (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={redistributeCredits}
                        className="text-xs"
                      >
                        Redistribute Equally
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={addDeviceSlot}
                        className="text-xs"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Device
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  {allocations.map((allocation, index) => (
                    <div key={index} className={`flex items-center gap-3 p-3 rounded-lg border ${isAlreadyAllocated ? 'bg-gray-50' : 'bg-white'}`}>
                      <div className="flex-1">
                        <Label className="text-xs text-muted-foreground">Device {index + 1}</Label>
                        
                        {isAlreadyAllocated ? (
                          <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-100 text-sm">
                            <div className="font-medium text-gray-900">
                              {allocation.deviceName || allocation.deviceId}
                            </div>
                            <div className="text-xs text-gray-600">
                              ID: {allocation.deviceId}
                            </div>
                          </div>
                        ) : (
                          <select
                            value={allocation.deviceId}
                            onChange={(e) => handleDeviceChange(index, e.target.value)}
                            className="w-full mt-1 p-2 border border-gray-300 rounded-md text-sm"
                            disabled={isAlreadyAllocated}
                          >
                            <option value="">
                              {isCreditsOnlyMode ? "Select customer's device..." : "Select an unallocated device..."}
                            </option>
                            {(isCreditsOnlyMode ? customerDevices : unallocatedDevices).map((device) => (
                              <option key={device.device_id} value={device.device_id}>
                                {device.device_name || device.device_description || device.device_id}
                              </option>
                            ))}
                          </select>
                        )}
                        
                        {!isAlreadyAllocated && allocation.deviceId && (
                          <div className="mt-1 flex items-center">
                            <Badge variant="secondary" className="ml-2 text-xs">
                              {isCreditsOnlyMode ? "Customer Device" : "Unallocated Device"}
                            </Badge>
                          </div>
                        )}
                      </div>
                      
                      <div className="w-32">
                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                          <Image className="h-3 w-3" />
                          Credits
                        </Label>
                        {isAlreadyAllocated ? (
                          <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-100 text-sm font-medium text-center">
                            {isNaN(allocation.allocatedCredits) ? '0' : allocation.allocatedCredits.toLocaleString()}
                          </div>
                        ) : (
                          <Input
                            type="number"
                            min="0"
                            value={isNaN(allocation.allocatedCredits) ? '' : allocation.allocatedCredits}
                            onChange={(e) => handleCreditsChange(index, parseInt(e.target.value) || 0)}
                            className="mt-1 text-sm"
                            disabled={allocation.isLocked || isAlreadyAllocated}
                          />
                        )}
                      </div>

                      {!isAlreadyAllocated && !isCreditsOnlyMode && (
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleLock(index)}
                            className={`text-xs ${allocation.isLocked ? 'text-orange-600 border-orange-300' : 'text-gray-600'}`}
                          >
                            {allocation.isLocked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                          </Button>
                          
                          {allocations.length > 1 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => removeDeviceSlot(index)}
                              className="text-red-600 hover:text-red-700 hover:border-red-300 text-xs"
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-3 border-t">
                  {!isNaN(totalAllocated) && !isNaN(totalImages) && (
                    <div className="text-sm space-y-1">
                      <div>
                        <span className="text-muted-foreground">Total Allocated: </span>
                        <span className={totalAllocated === totalImages ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                          {totalAllocated.toLocaleString()} / {totalImages.toLocaleString()}
                        </span>
                      </div>
                      {remainingCredits !== 0 && !isNaN(remainingCredits) && (
                        <div>
                          <span className="text-muted-foreground">Remaining: </span>
                          <span className="text-orange-600 font-medium">
                            {remainingCredits.toLocaleString()} credits
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <Button 
                    onClick={handleSave}
                    disabled={
                      !isValid || 
                      saveDeviceAllocation.isPending || 
                      isAlreadyAllocated ||
                      (isCreditsOnlyMode && customerDevices.length === 0)
                    }
                    className={isAlreadyAllocated ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"}
                  >
                    {saveDeviceAllocation.isPending 
                      ? "Saving..." 
                      : isAlreadyAllocated 
                        ? "Already Allocated" 
                        : isCreditsOnlyMode && customerDevices.length === 0
                          ? "No Customer Devices"
                          : "Save Allocation"
                    }
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
